import type { TFunction } from 'i18next'
import {
  FRAME_COLORS,
  MATERIAL_TYPES,
  TILE_SIZES,
  getTileSizeSurface,
  type PricingProduct,
} from '@mixtiles/web-backend-shared'
import { getSizeName } from 'services/SizesManager/SizesManagerHelper'
import { getFrameColorName } from 'utils/colorUtils'
import type { TilesCartItem } from 'stores/cartStore/CartItem'
import type { GroupPricingProduct } from './groupCheckoutPricing.types'

export type BreakdownRowItem = {
  size: string
  label: string
  priceInfo: { originalPrice: number; discountedPrice: number } | undefined
  frame: string
}

function buildPriceInfo(sizeProducts: GroupPricingProduct[] | undefined) {
  if (!sizeProducts?.length) return undefined
  return {
    originalPrice: sizeProducts.reduce(
      (sum, p) => sum + (p.priceBeforeDiscount ?? 0),
      0
    ),
    discountedPrice: sizeProducts.reduce(
      (sum, p) => sum + (p.priceAfterDiscount ?? 0),
      0
    ),
  }
}

function getMatchingPricingProducts(
  product: PricingProduct,
  pricingProducts?: GroupPricingProduct[]
): GroupPricingProduct[] | undefined {
  return pricingProducts?.filter(
    (p) =>
      p.pricingProduct?.tileSize === product.tileSize &&
      p.pricingProduct?.materialType === product.materialType &&
      p.pricingProduct?.frameColor === product.frameColor
  )
}

type TilesPricingProductEntry =
  TilesCartItem['metadata']['pricingProducts'][number]

function buildFrameBreakdownItem(
  entry: TilesPricingProductEntry,
  t: TFunction,
  pricingCountry: string,
  pricingProducts?: GroupPricingProduct[]
): BreakdownRowItem {
  const { tileSize } = entry.pricingProduct
  const sizeName = getSizeName({ size: tileSize, t, pricingCountry })
  const priceInfo = buildPriceInfo(
    getMatchingPricingProducts(entry.pricingProduct, pricingProducts)
  )

  if (tileSize === TILE_SIZES.RECTANGLE_25X8) {
    const wallSigns = t(
      entry.quantity === 1
        ? 'order.cart_summary.wall_signs_one'
        : 'order.cart_summary.wall_signs_other',
      { count: entry.quantity }
    )
    const label = t('order.cart_summary.with_size', {
      item: wallSigns,
      size: sizeName,
    })
    return { size: tileSize, frame: 'wall-sign', label, priceInfo }
  }

  const { frameColor, materialType } = entry.pricingProduct
  const frame = getFrameColorName(
    frameColor ?? FRAME_COLORS.NONE,
    materialType === MATERIAL_TYPES.WIDE_FRAME,
    materialType,
    t
  )
  const label =
    materialType === MATERIAL_TYPES.CANVAS
      ? t('order.cart_summary.canvas_breakdown_row', {
          count: entry.quantity,
          frame,
          size: sizeName,
        })
      : t(
          entry.quantity === 1
            ? 'order.cart_summary.frame_breakdown_row_one'
            : 'order.cart_summary.frame_breakdown_row_many',
          { count: entry.quantity, frame, size: sizeName }
        )
  return { size: tileSize, frame, label, priceInfo }
}

export function buildFrameBreakdownItems(
  item: TilesCartItem,
  t: TFunction,
  pricingCountry: string,
  pricingProducts?: GroupPricingProduct[]
): BreakdownRowItem[] {
  return [...item.metadata.pricingProducts]
    .sort(
      (a, b) =>
        getTileSizeSurface(a.pricingProduct.tileSize) -
        getTileSizeSurface(b.pricingProduct.tileSize)
    )
    .map((entry) =>
      buildFrameBreakdownItem(entry, t, pricingCountry, pricingProducts)
    )
}

/**
 * Splits a breakdown label like '3 Tiles (8''x8'')' into segments so that
 * parenthesized or quoted parts (the size) can be styled lighter than the
 * count portion.
 */
export function splitLabel(label: string): {
  parts: { text: string; light: boolean }[]
} {
  const segments: { text: string; light: boolean }[] = []
  const regex = /(\([^)]*\)|"[^"]*"|"[^"]*")/g
  let lastIndex = 0
  let match = regex.exec(label)

  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: label.slice(lastIndex, match.index), light: false })
    }
    segments.push({ text: match[0], light: true })
    lastIndex = regex.lastIndex
    match = regex.exec(label)
  }

  if (lastIndex < label.length) {
    segments.push({ text: label.slice(lastIndex), light: false })
  }

  if (segments.length === 0) {
    segments.push({ text: label, light: false })
  }

  return { parts: segments }
}
